/**
 * Spotify Web API Service
 * 
 * Provides methods to interact with Spotify Web API
 * - Fetch user's playlists
 * - Fetch saved tracks/albums
 * - Search for music
 * - Get recommendations
 */

import { spotifyAuth } from './SpotifyAuth';

const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';

class SpotifyAPIService {
  /**
   * Make authenticated API request to Spotify
   */
  async apiRequest(endpoint, options = {}) {
    let token = spotifyAuth.getAccessToken();
    
    if (!token) {
      // Try to refresh
      try {
        token = await spotifyAuth.refreshAccessToken();
      } catch {
        throw new Error('Not authenticated with Spotify');
      }
    }

    const url = endpoint.startsWith('http') ? endpoint : `${SPOTIFY_API_BASE}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh token and retry
        try {
          token = await spotifyAuth.refreshAccessToken();
          const retryResponse = await fetch(url, {
            ...options,
            headers: {
              'Authorization': `Bearer ${token}`,
              ...options.headers
            }
          });
          if (retryResponse.ok) {
            return retryResponse.json();
          }
        } catch {
          spotifyAuth.clearToken();
          throw new Error('Spotify session expired');
        }
      }
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || `Spotify API error: ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  /**
   * Get user's playlists
   */
  async getMyPlaylists(limit = 50, offset = 0) {
    const data = await this.apiRequest(`/me/playlists?limit=${limit}&offset=${offset}`);

    return {
      items: data.items?.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        image: item.images?.[0]?.url,
        trackCount: item.tracks?.total || 0,
        owner: item.owner?.display_name,
        isPublic: item.public,
        uri: item.uri,
        source: 'spotify'
      })) || [],
      total: data.total,
      next: data.next
    };
  }

  /**
   * Get tracks from a playlist
   */
  async getPlaylistTracks(playlistId, limit = 50, offset = 0) {
    const data = await this.apiRequest(`/playlists/${playlistId}/tracks?limit=${limit}&offset=${offset}`);

    return {
      items: data.items?.map(item => ({
        id: item.track?.id,
        name: item.track?.name,
        artist: item.track?.artists?.map(a => a.name).join(', '),
        artistId: item.track?.artists?.[0]?.id,
        album: item.track?.album?.name,
        albumId: item.track?.album?.id,
        albumReleaseDate: item.track?.album?.release_date, // Add release date for year extraction
        albumType: item.track?.album?.album_type, // 'album', 'single', 'compilation', etc.
        image: item.track?.album?.images?.[0]?.url,
        duration: item.track?.duration_ms,
        uri: item.track?.uri,
        previewUrl: item.track?.preview_url,
        addedAt: item.added_at,
        source: 'spotify'
      })).filter(item => item.id) || [],
      total: data.total,
      next: data.next
    };
  }


  /**
   * Get user's saved tracks (Liked Songs)
   */
  async getSavedTracks(limit = 50, offset = 0) {
    const data = await this.apiRequest(`/me/tracks?limit=${limit}&offset=${offset}`);

    return {
      items: data.items?.map(item => ({
        id: item.track?.id,
        name: item.track?.name,
        artist: item.track?.artists?.map(a => a.name).join(', '),
        artistId: item.track?.artists?.[0]?.id,
        album: item.track?.album?.name,
        albumId: item.track?.album?.id,
        image: item.track?.album?.images?.[0]?.url,
        duration: item.track?.duration_ms,
        uri: item.track?.uri,
        previewUrl: item.track?.preview_url,
        addedAt: item.added_at,
        source: 'spotify'
      })) || [],
      total: data.total,
      next: data.next
    };
  }

  /**
   * Get user's saved albums
   */
  async getSavedAlbums(limit = 50, offset = 0) {
    const data = await this.apiRequest(`/me/albums?limit=${limit}&offset=${offset}`);

    return {
      items: data.items?.map(item => ({
        id: item.album?.id,
        name: item.album?.name,
        artist: item.album?.artists?.map(a => a.name).join(', '),
        image: item.album?.images?.[0]?.url,
        trackCount: item.album?.total_tracks,
        releaseDate: item.album?.release_date,
        uri: item.album?.uri,
        addedAt: item.added_at,
        source: 'spotify'
      })) || [],
      total: data.total,
      next: data.next
    };
  }

  /**
   * Get album tracks
   */
  async getAlbumTracks(albumId) {
    const [albumData, tracksData] = await Promise.all([
      this.apiRequest(`/albums/${albumId}`),
      this.apiRequest(`/albums/${albumId}/tracks?limit=50`)
    ]);

    return {
      album: {
        id: albumData.id,
        name: albumData.name,
        artist: albumData.artists?.map(a => a.name).join(', '),
        image: albumData.images?.[0]?.url,
        releaseDate: albumData.release_date,
        trackCount: albumData.total_tracks
      },
      tracks: tracksData.items?.map(track => ({
        id: track.id,
        name: track.name,
        artist: track.artists?.map(a => a.name).join(', '),
        duration: track.duration_ms,
        trackNumber: track.track_number,
        uri: track.uri,
        previewUrl: track.preview_url,
        source: 'spotify'
      })) || []
    };
  }

  /**
   * Get user's recently played tracks
   */
  async getRecentlyPlayed(limit = 50) {
    const data = await this.apiRequest(`/me/player/recently-played?limit=${limit}`);

    return {
      items: data.items?.map(item => ({
        id: item.track?.id,
        name: item.track?.name,
        artist: item.track?.artists?.map(a => a.name).join(', '),
        album: item.track?.album?.name,
        image: item.track?.album?.images?.[0]?.url,
        duration: item.track?.duration_ms,
        uri: item.track?.uri,
        previewUrl: item.track?.preview_url,
        playedAt: item.played_at,
        source: 'spotify'
      })) || []
    };
  }

  /**
   * Get user's top tracks
   */
  async getTopTracks(timeRange = 'medium_term', limit = 50) {
    const data = await this.apiRequest(`/me/top/tracks?time_range=${timeRange}&limit=${limit}`);

    return {
      items: data.items?.map(track => ({
        id: track.id,
        name: track.name,
        artist: track.artists?.map(a => a.name).join(', '),
        album: track.album?.name,
        image: track.album?.images?.[0]?.url,
        duration: track.duration_ms,
        uri: track.uri,
        previewUrl: track.preview_url,
        source: 'spotify'
      })) || []
    };
  }

  /**
   * Search Spotify
   */
  async search(query, types = ['track'], limit = 20) {
    const typeParam = types.join(',');
    const data = await this.apiRequest(`/search?q=${encodeURIComponent(query)}&type=${typeParam}&limit=${limit}`);

    const results = {};

    if (data.tracks) {
      results.tracks = data.tracks.items?.map(track => ({
        id: track.id,
        name: track.name,
        artist: track.artists?.map(a => a.name).join(', '),
        album: track.album?.name,
        image: track.album?.images?.[0]?.url,
        duration: track.duration_ms,
        uri: track.uri,
        previewUrl: track.preview_url,
        source: 'spotify'
      })) || [];
    }

    if (data.albums) {
      results.albums = data.albums.items
        ?.filter(album => album != null) // Filter out null items
        ?.map(album => ({
          id: album.id,
          name: album.name,
          artist: album.artists?.map(a => a.name).join(', '),
          image: album.images?.[0]?.url,
          releaseDate: album.release_date,
          uri: album.uri,
          source: 'spotify'
        })) || [];
    }

    if (data.artists) {
      results.artists = data.artists.items
        ?.filter(artist => artist != null) // Filter out null items
        ?.map(artist => ({
          id: artist.id,
          name: artist.name,
          image: artist.images?.[0]?.url,
          followers: artist.followers?.total,
          genres: artist.genres,
          uri: artist.uri,
          source: 'spotify'
        })) || [];
    }

    if (data.playlists) {
      results.playlists = data.playlists.items
        ?.filter(playlist => playlist != null) // Filter out null items
        ?.map(playlist => ({
          id: playlist.id,
          name: playlist.name,
          image: playlist.images?.[0]?.url,
          owner: playlist.owner?.display_name,
          trackCount: playlist.tracks?.total,
          uri: playlist.uri,
          source: 'spotify'
        })) || [];
    }

    return results;
  }

  /**
   * Format duration from milliseconds to mm:ss
   */
  formatDuration(ms) {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Get user's music library summary
   */
  async getLibrarySummary() {
    const [playlists, savedTracks, savedAlbums, recentlyPlayed] = await Promise.all([
      this.getMyPlaylists(5),
      this.getSavedTracks(5),
      this.getSavedAlbums(5),
      this.getRecentlyPlayed(5)
    ]);

    return {
      playlists: playlists.items,
      totalPlaylists: playlists.total,
      savedTracks: savedTracks.items,
      totalSavedTracks: savedTracks.total,
      savedAlbums: savedAlbums.items,
      totalSavedAlbums: savedAlbums.total,
      recentlyPlayed: recentlyPlayed.items
    };
  }
}

// Export singleton instance
export const spotifyAPI = new SpotifyAPIService();
export default spotifyAPI;

